import { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApolloQueryResult,
  OperationVariables,
  useMutation,
  useQuery,
} from '@apollo/client';
import { removeCompanyRole, getAvailableCompanyRoles } from 'graphql-queries';
import {
  AvailableCompanyRole,
  CompanyRole,
  RoleOption,
} from './types';

type UseRemoveCompanyRoleProps = {
  selectedUser: {
    id: string;
    name: string;
    email: string;
    companyRoles: CompanyRole[];
  };
  setModalClose: (value: boolean) => void;
  refetch: (
    variables?: Partial<OperationVariables> | undefined
  ) => Promise<ApolloQueryResult<unknown>>;
  channelId: string;
  isModalOpen: boolean;
};

export const useRemoveCompanyRole = ({
  selectedUser,
  setModalClose,
  refetch,
  channelId,
  isModalOpen,
}: UseRemoveCompanyRoleProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<RoleOption[]>([]);

  const [removeCompanyRoleMutation] = useMutation(removeCompanyRole);

  // Fetch available company roles from GraphQL
  const {
    data: rolesData,
    loading: rolesLoading,
    error: rolesError,
  } = useQuery(getAvailableCompanyRoles, {
    variables: {
      propertyId: channelId,
    },
    skip: !isModalOpen,
    fetchPolicy: 'cache-and-network',
  });

  // Reset selected roles when selected user changes to prevent stale selections
  useEffect(() => {
    setSelectedRoles([]);
  }, [selectedUser.id]);

  const onCloseModal = useCallback(() => {
    setModalClose(false);
    setSelectedRoles([]);
  }, [setModalClose]);

  const handleSelectRoles = useCallback((values: RoleOption[]) => {
    setSelectedRoles(values);
  }, []);

  const handleRemoveRoles = useCallback(async () => {
    if (selectedRoles.length === 0) return;

    setLoading(true);

    try {
      await removeCompanyRoleMutation({
        variables: {
          userIds: [selectedUser.id],
          roleTemplateIds: selectedRoles.map(role => role.value),
          channelId,
        },
      });

      await refetch();
      window.Toast.show(
        t('web.admin.channel.users.all.table.removeCompanyRole.success'),
        5000
      );
      onCloseModal();
    } catch (err) {
      await window.Alert.alert({
        title: t('web.admin.channel.users.all.table.removeCompanyRole.error'),
        error: err,
      });
    }

    setLoading(false);
  }, [
    selectedRoles,
    refetch,
    t,
    onCloseModal,
    channelId,
    removeCompanyRoleMutation,
    selectedUser.id,
  ]);

  // Map available property roles to dropdown options
  const roleOptions: RoleOption[] = useMemo(() => {
    const availableRoles = rolesData?.getAvailableCompanyRoles ?? [];

    return availableRoles.map((role: AvailableCompanyRole) => ({
      label: role.name,
      value: role.templateId,
    }));
  }, [rolesData]);

  // Get affected tenants based on selected roles
  const affectedTenants = useMemo(() => {
    if (!selectedRoles.length || !selectedUser.companyRoles?.length) return [];

    const selectedRoleNames = new Set(selectedRoles.map(r => r.label));
    const tenantNames = new Set<string>();

    selectedUser.companyRoles.forEach(cr => {
      const hasSelectedRole = cr.roles.some(r => selectedRoleNames.has(r.name));

      if (hasSelectedRole) {
        tenantNames.add(cr.companyName);
      }
    });

    return Array.from(tenantNames);
  }, [selectedRoles, selectedUser.companyRoles]);

  const hasNoRoles = roleOptions.length === 0 && !rolesLoading;
  const isButtonDisabled =
    loading || rolesLoading || selectedRoles.length === 0 || hasNoRoles;

  return {
    loading: loading || rolesLoading,
    rolesError,
    selectedRoles,
    roleOptions,
    affectedTenants,
    hasNoRoles,
    isButtonDisabled,
    onCloseModal,
    handleSelectRoles,
    handleRemoveRoles,
  };
};

